import {
  Box,
  Text,
  Divider,
  Pagination,
  Stack,
  Spread,
} from 'braid-design-system';
import { Fragment } from 'react';

import type { ProfileCardView } from '../../types/profile.types';
import { EthicalHiringToggle } from '../EthicalHiringToggle/EthicalHiringToggle';
import type { LoginPromptProps } from '../GenericLoginPrompt/GenericLoginPrompt';
import { ProfileCard } from '../Profile/ProfileCard/ProfileCard';

import { countTextWrapperStyle } from './ProfileList.css';

export type HideNamesState = {
  isHideNamesChecked: boolean;
  onChange: (value: boolean) => void;
};

interface Props<T extends ProfileCardView = ProfileCardView> {
  showHideNamesToggle?: boolean;
  hideNamesState?: HideNamesState;
  profiles: T[];
  isAuthenticated: boolean;
  countText: string;
  loginUrl: string;
  bottomActionBanner?: React.ReactNode;
  paginationProps?: React.ComponentProps<typeof Pagination>;
  renderNoResults?: () => React.ReactNode;
  renderCardActions?: (profile: T) => React.ReactNode;
  onProfileClick?: (profileSlug: string) => void;
  trackingCallbacks: LoginPromptProps['trackingCallbacks'];
  getProfileViewLink: (profile: T) => string;
  onLastInteractionClick?: (profileSlug: string) => void;
}

export const ProfileList = <T extends ProfileCardView = ProfileCardView>({
  showHideNamesToggle,
  hideNamesState,
  profiles,
  countText,
  isAuthenticated,
  loginUrl,
  bottomActionBanner,
  paginationProps,
  renderNoResults,
  renderCardActions,
  onProfileClick,
  trackingCallbacks,
  getProfileViewLink,
  onLastInteractionClick,
}: Props<T>) => {
  const hasProfiles = profiles.length > 0;

  return (
    <>
      <Box
        paddingX={{ mobile: 'small', tablet: 'medium' }}
        display="flex"
        alignItems="center"
        paddingY="medium"
        className={countTextWrapperStyle}
      >
        <Spread space="large" alignY="center">
          <Text size="small" weight="strong">
            {countText}
          </Text>
          {showHideNamesToggle && hideNamesState ? (
            <EthicalHiringToggle
              isHideNamesChecked={hideNamesState.isHideNamesChecked}
              onChange={hideNamesState.onChange}
            />
          ) : null}
        </Spread>
      </Box>

      {hasProfiles ? (
        <Stack space="none">
          {profiles.map((profile, index) => (
            <Fragment key={profile.slug}>
              <ProfileCard
                renderActions={renderCardActions}
                profile={profile}
                isAuthenticated={isAuthenticated}
                loginUrl={loginUrl}
                profileViewLink={getProfileViewLink(profile)}
                onProfileClick={onProfileClick}
                trackingCallbacks={trackingCallbacks}
                onLastInteractionClick={onLastInteractionClick}
              />

              {index < profiles.length - 1 && <Divider />}
            </Fragment>
          ))}
        </Stack>
      ) : (
        renderNoResults?.()
      )}

      {bottomActionBanner && (
        <>
          <Divider />
          {bottomActionBanner}
        </>
      )}

      {paginationProps && (
        <>
          <Divider />
          <Box paddingY="small">
            <Pagination {...paginationProps} />
          </Box>
        </>
      )}
    </>
  );
};
